package com.writerslab

import androidx.compose.runtime.compositionLocalOf
import kotlinx.serialization.Serializable

/**
 * Mirror of the backend `Pricing` object — deserialised from
 * `GET /api/pricing` on app bootstrap. Defaults match the backend so
 * the UI still renders sensible numbers if the bootstrap fetch fails.
 *
 * Read at call sites via `LocalPricing.current.someField`. Never
 * hardcode the same numbers a second time in component code — change
 * the backend object, restart, frontend picks up the new values from
 * the next GET /api/pricing.
 */
@Serializable
data class Pricing(
    val analysisCharsPerCredit: Int = 150_000,
    val annotationCost: Int = 1,
    val coverRegenCost: Int = 1,
    val teaserPreviewCost: Int = 1,
    val teaserRenderCost: Int = 4,
    /** Render cost when the backend routes i2v to the cheap
     *  fallback model instead of the production one. */
    val teaserRenderLowQualityCost: Int = 1,
    /** Lifetime free pool per user — first N annotations free. */
    val freeAnnotationsPerUser: Int = 1,
    /** Lifetime free pool per user — first N full cover-sets free. */
    val freeCoverBatchesPerUser: Int = 2,
    /** Lifetime free pool per user — first N scene previews free. */
    val freeTeaserPreviewsPerUser: Int = 2,
    /** AI-highlight scan: 1 credit per this many chars (preview = 1 unit,
     *  full = ceil(chars / this)). */
    val aiHighlightCharsPerCredit: Int = 50_000,
    /** Lifetime free pool per user — first N AI-highlight previews free. */
    val freeAiHighlightPreviewsPerUser: Int = 3,
    /** One-time credits granted on first sign-up (backend FREE_CREDITS_AMOUNT).
     *  Shown in the anonymous "sign in for N free credits" banner. */
    val signupBonusCredits: Int = 10,
    // Credit pack catalogue — loaded from prices.yml via /api/pricing.
    // These are gross prices (what the buyer pays); backend handles the
    // net split internally. Credits count is also server-authoritative.
    val packStartCredits:   Int    = 10,
    val packStartPriceRub:  Double = 444.0,
    val packStartPriceUsd:  Double = 9.0,
    val packMediumCredits:  Int    = 50,
    val packMediumPriceRub: Double = 1776.0,
    val packMediumPriceUsd: Double = 36.0,
    val packLargeCredits:   Int    = 200,
    val packLargePriceRub:  Double = 5550.0,
    val packLargePriceUsd:  Double = 108.0,
) {
    /** Look up gross RUB price by pack id. Used in analytics after
     *  payment confirmation when the Compose tree isn't available. */
    fun packPriceRub(packId: String): Double? = when (packId) {
        "start"  -> packStartPriceRub
        "medium" -> packMediumPriceRub
        "large"  -> packLargePriceRub
        else     -> null
    }
}

/**
 * CompositionLocal for pricing — same pattern as `LocalStrings`.
 * `Main.kt` provides the value from the VM after fetch; defaults
 * cover the brief window between page load and bootstrap completion.
 */
val LocalPricing = compositionLocalOf { Pricing() }
